"use client";

import React from "react";
import { motion } from "framer-motion";
import clsx from "clsx";

/**
 * Section Component
 * 
 * A consistent container wrapper for all page sections.
 * Aligns with the PublicBackground 6-column grid system.
 * 
 * Grid System:
 * - 6 columns at 16.67% each on desktop (lg+)
 * - Content typically spans columns 2-5 for normal sections
 * - extendRight allows content to span from column 2 to edge (columns 2-6)
 * 
 * Usage:
 * <Section>Content here</Section>
 * <Section spacing="tight">Tight spacing</Section>
 * <Section bgColor="#f8fafc">Full-width colored bg</Section>
 * <Section extendRight>Content extends to right edge</Section>
 */

interface SectionProps {
  children: React.ReactNode;
  className?: string;
  
  /** Content width: 'full' = edge to edge, 'wide' = max-w-7xl, 'narrow' = max-w-5xl */
  size?: "full" | "wide" | "narrow";
  
  /** Vertical spacing: 'none' | 'tight' | 'normal' | 'relaxed' */
  spacing?: "none" | "tight" | "normal" | "relaxed";
  
  /** Override top padding specifically (e.g., 'pt-2', 'pt-0') */
  topPadding?: string;
  
  /** Override bottom padding specifically (e.g., 'pb-2', 'pb-0') */
  bottomPadding?: string;
  
  /** If true, removes horizontal padding */
  fullWidth?: boolean;
  
  /** Background style preset */
  background?: "transparent" | "white" | "subtle" | "themed";
  
  /** Custom background color (overrides background preset) - full width */
  bgColor?: string;
  
  /** If true, adds solid bg to hide grid lines */
  hideGrid?: boolean;
  
  /** Enable fade-in animation */
  animate?: boolean;
  
  /** HTML element to render */
  as?: "section" | "div" | "article";
  
  /** If true, allows content to extend to the right edge of the screen (desktop only) */
  extendRight?: boolean;
}

const spacingClasses = {
  none: "py-0",
  tight: "py-4 sm:py-6 lg:py-8",
  normal: "py-8 sm:py-10 lg:py-12",
  relaxed: "py-12 sm:py-16 lg:py-20",
};

// 6-column grid: each column is 16.67% of screen width
// Content area spans columns 2-5 (from 16.67% to 83.33% = 66.66% width)
const sizeClasses = {
  full: "",
  wide: "", // Handled by grid alignment below
  narrow: "",
};

export function Section({
  children,
  className = "",
  size = "wide",
  spacing = "normal",
  topPadding,
  bottomPadding,
  fullWidth = false,
  background = "transparent",
  bgColor,
  hideGrid = false,
  animate = true,
  as: Component = "section",
  extendRight = false,
}: SectionProps) {
  // Determine background
  const getBgClass = () => {
    if (bgColor || hideGrid) return "";
    switch (background) {
      case "white": return "bg-white";
      case "subtle": return "bg-gray-50";
      default: return "";
    }
  };

  // Responsive Grid Layout:
  // - Mobile: full width with side padding (px-4)
  // - Tablet: full width with side padding (px-6)  
  // - Desktop: 6-column grid, content in columns 2-5 (margins 16.67% each side)
  // - extendRight: full width, child component handles its own grid alignment
  
  const content = extendRight ? (
    // ExtendRight: child component (like PageHero) handles its own grid alignment
    <div className={clsx("w-full", className)}>
      {children}
    </div>
  ) : (
    // Normal: responsive padding on mobile/tablet, grid margins on desktop
    <div className={clsx("w-full", className)}>
      <div className="px-4 sm:px-6 xl:px-0 xl:mx-[16.67%]">
        {children}
      </div>
    </div>
  );

  // Build spacing classes - allow individual padding overrides
  const getSpacingClasses = () => {
    const baseSpacing = spacingClasses[spacing];
    
    // If no overrides, use the preset
    if (!topPadding && !bottomPadding) {
      return baseSpacing;
    }
    
    // Extract responsive classes from preset and override as needed
    // Default presets structure: "py-X sm:py-X lg:py-X"
    // We need to convert to individual pt/pb when overriding
    const spacingMap = {
      none: { top: "pt-0", bottom: "pb-0" },
      tight: { top: "pt-4 sm:pt-6 lg:pt-8", bottom: "pb-4 sm:pb-6 lg:pb-8" },
      normal: { top: "pt-8 sm:pt-10 lg:pt-12", bottom: "pb-8 sm:pb-10 lg:pb-12" },
      relaxed: { top: "pt-12 sm:pt-16 lg:pt-20", bottom: "pb-12 sm:pb-16 lg:pb-20" },
    };
    
    const preset = spacingMap[spacing];
    return clsx(
      topPadding || preset.top,
      bottomPadding || preset.bottom
    );
  };

  const sectionClasses = clsx(
    "w-full relative",
    getSpacingClasses(),
    getBgClass()
  );

  // Custom style for bgColor or hideGrid
  const sectionStyle: React.CSSProperties | undefined = bgColor 
    ? { backgroundColor: bgColor }
    : hideGrid 
      ? { backgroundColor: "white" }
      : background === "themed" 
        ? { background: 'linear-gradient(135deg, color-mix(in srgb, var(--mininav-active-color) 3%, white) 0%, white 50%, color-mix(in srgb, var(--mininav-active-color) 2%, white) 100%)' }
        : undefined;

  if (animate) {
    return (
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        viewport={{ once: true, margin: "-50px" }}
        className={sectionClasses}
        style={sectionStyle}
      >
        {content}
      </motion.section>
    );
  }

  return (
    <Component className={sectionClasses} style={sectionStyle}>
      {content}
    </Component>
  );
}

/**
 * Grid Component
 * 
 * A flexible grid that aligns with the PublicBackground 6-column grid lines.
 * Use inside Section for consistent alignment.
 * 
 * Usage:
 * <Section>
 *   <Grid cols={4}>
 *     <GridItem>Col 1</GridItem>
 *     <GridItem>Col 2</GridItem>
 *     <GridItem>Col 3</GridItem>
 *     <GridItem>Col 4</GridItem>
 *   </Grid>
 * </Section>
 */

interface GridProps {
  children: React.ReactNode;
  className?: string;
  /** Number of columns: 1, 2, 3, or 4 */
  cols?: 1 | 2 | 3 | 4;
  /** Gap size */
  gap?: "none" | "sm" | "md" | "lg";
}

const gapClasses = {
  none: "gap-0",
  sm: "gap-4 sm:gap-6",
  md: "gap-6 sm:gap-8",
  lg: "gap-8 sm:gap-10 lg:gap-12",
};

const colClasses = {
  1: "grid-cols-1",
  2: "grid-cols-1 md:grid-cols-2",
  3: "grid-cols-1 md:grid-cols-2 xl:grid-cols-3",
  4: "grid-cols-1 md:grid-cols-2 xl:grid-cols-4",
};

export function Grid({ children, className = "", cols = 4, gap = "md" }: GridProps) {
  return (
    <div className={clsx("grid", colClasses[cols], gapClasses[gap], className)}>
      {children}
    </div>
  );
}

/**
 * GridItem Component
 * 
 * Individual grid item with optional span.
 */
interface GridItemProps {
  children: React.ReactNode;
  className?: string;
  /** Span multiple columns on desktop */
  span?: 1 | 2 | 3 | 4;
}

const spanClasses = {
  1: "xl:col-span-1",
  2: "xl:col-span-2",
  3: "xl:col-span-3",
  4: "xl:col-span-4",
};

export function GridItem({ children, className = "", span = 1 }: GridItemProps) {
  return (
    <div className={clsx(spanClasses[span], className)}>
      {children}
    </div>
  );
}

export default Section;
